import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Languages, Save, X } from "lucide-react";
import TranslationsEditor from "@/components/TranslationsEditor";

// Admin editor for initiative text (title, tagline, short, summary) in English,
// plus ML/HI via the shared TranslationsEditor. Edits mark the initiative as
// admin-owned so the startup seed-sync preserves them across restarts.
const FIELDS = [
  ["title", "Title", false],
  ["tagline", "Tagline", true],
  ["short", "Short description (cards)", true],
  ["summary", "Summary (initiative page)", true],
];

export default function InitiativesAdmin() {
  const [items, setItems] = useState([]);
  const [editSlug, setEditSlug] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [trDoc, setTrDoc] = useState(null);

  const refresh = () => api.get("/initiatives").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const openEdit = (it) => {
    setEditSlug(it.slug);
    setForm({ title: it.title || "", tagline: it.tagline || "", short: it.short || "", summary: it.summary || "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/initiatives/${editSlug}`, form);
      toast.success("Initiative updated — live on the site");
      setEditSlug(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="initiatives-admin">
      <p className="text-sm text-ink/60 max-w-2xl">
        Edit the description text for each centre and programme. English edits go live immediately; use the translations
        button to provide Malayalam and Hindi.
      </p>
      <ul className="space-y-2 list-none p-0" data-testid="initiatives-admin-list">
        {items.map((it) => (
          <li key={it.slug} data-testid={`init-row-${it.slug}`} className="border border-ink/10 bg-white p-4 flex items-start gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-display font-bold text-brand text-sm">{it.title}</p>
                <span className="font-mono text-[9px] uppercase tracking-widest text-ink/40">{it.category}</span>
              </div>
              <p className="mt-1 text-ink/70 text-xs leading-relaxed">{it.short || it.tagline}</p>
            </div>
            <button onClick={() => openEdit(it)} data-testid={`init-edit-${it.slug}`} className="text-ink/60 hover:text-brand text-xs border border-ink/10 px-2 py-1">Edit</button>
            <button onClick={() => setTrDoc(it)} data-testid={`init-tr-${it.slug}`} className="text-teal hover:text-teal-dark" title="Edit translations" aria-label="Edit translations">
              <Languages className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>

      {editSlug && (
        <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-surface border border-ink/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="init-edit-modal">
            <div className="flex items-start justify-between p-5 border-b border-ink/10">
              <h2 className="font-display font-bold text-brand text-lg">Edit initiative</h2>
              <button data-testid="init-edit-close" onClick={() => setEditSlug(null)} aria-label="Close" className="text-ink/60 hover:text-ink"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              {FIELDS.map(([k, label, multi]) => (
                <label key={k} className="block">
                  <span className="block text-xs font-mono uppercase tracking-widest text-ink/50 mb-1">{label}</span>
                  {multi ? (
                    <textarea data-testid={`init-field-${k}`} rows={3} value={form[k] || ""}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="w-full bg-base border border-ink/10 focus:border-teal outline-none px-3 py-2 text-ink text-sm resize-none" />
                  ) : (
                    <input data-testid={`init-field-${k}`} value={form[k] || ""}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="w-full bg-base border border-ink/10 focus:border-teal outline-none px-3 py-2 text-ink text-sm" />
                  )}
                </label>
              ))}
            </div>
            <div className="p-5 border-t border-ink/10">
              <button data-testid="init-save" onClick={save} disabled={saving}
                className="w-full bg-brand hover:bg-brand-dark disabled:opacity-50 text-white py-2.5 flex items-center justify-center gap-2">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <TranslationsEditor
        open={!!trDoc}
        doc={trDoc}
        collection="initiatives"
        fields={["title", "tagline", "short", "summary"]}
        onClose={() => setTrDoc(null)}
        onSaved={() => refresh()}
      />
    </div>
  );
}
